import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from "recharts";

export type NbsDonutSegment = {
  readonly name: string;
  readonly value: number;
};

export type NbsDonutChartProps = {
  readonly data: readonly NbsDonutSegment[];
  readonly height?: number;
  readonly colors?: readonly string[];
  readonly centerLabel?: string | null;
  readonly centerValue?: string | null;
  readonly centerUnit?: string | null;
  readonly unit?: string | null;
  /** When false, only the plot is shown (shared legend elsewhere). Default true. */
  readonly showLegend?: boolean;
};

const DEFAULT_COLORS = [
  "#000091",
  "#465fff",
  "#009081",
  "#6a6af4",
  "#e4794a",
  "#0063cb",
  "#18753c",
];

function formatValue(value: number): string {
  return new Intl.NumberFormat("fr-FR", {
    maximumFractionDigits: value >= 10 ? 1 : 2,
  }).format(value);
}

export function NbsDonutChart({
  data,
  height = 300,
  colors = DEFAULT_COLORS,
  centerLabel,
  centerValue,
  centerUnit,
  unit = "%",
  showLegend = true,
}: NbsDonutChartProps) {
  const rows = data.map((d) => ({ name: d.name, value: d.value }));

  return (
    <div className="nbs-donut-chart" data-testid="nbs-donut-chart">
      <div className="nbs-donut-chart__plot" style={{ height }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              data={rows}
              dataKey="value"
              nameKey="name"
              innerRadius="58%"
              outerRadius="82%"
              paddingAngle={1.5}
              isAnimationActive={false}
            >
              {rows.map((entry, i) => (
                <Cell
                  key={entry.name}
                  fill={colors[i % colors.length]}
                  stroke="#fff"
                  strokeWidth={1}
                />
              ))}
            </Pie>
            <Tooltip
              formatter={(value: number, name: string) => [
                unit ? `${formatValue(value)} ${unit}` : formatValue(value),
                name,
              ]}
            />
          </PieChart>
        </ResponsiveContainer>
        <div className="nbs-donut-chart__center" aria-hidden="true">
          {centerLabel ? (
            <span className="nbs-donut-chart__center-label">{centerLabel}</span>
          ) : null}
          {centerValue ? (
            <span className="nbs-donut-chart__center-value">{centerValue}</span>
          ) : null}
          {centerUnit ? <span className="nbs-donut-chart__center-unit">{centerUnit}</span> : null}
        </div>
      </div>
      {showLegend && rows.length > 0 ? (
        <ul className="nbs-donut-chart__legend" aria-label="Légende">
          {rows.map((entry, i) => (
            <li key={entry.name} className="nbs-donut-chart__legend-item">
              <span
                className="nbs-donut-chart__legend-swatch"
                style={{ background: colors[i % colors.length] }}
                aria-hidden="true"
              />
              <span className="nbs-donut-chart__legend-label">{entry.name}</span>
              <span className="nbs-donut-chart__legend-value">
                {formatValue(entry.value)}
                {unit ? ` ${unit}` : ""}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}
